import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface DisapprovedBadgeProps {
  /**
   * Die Issue-Codes aus `products.shopping_ads_issues`, so wie Google sie
   * meldet (`landing_page_error`, `missing_shipping`, …). Leer heisst
   * "abgelehnt, Grund nicht mitgeliefert" — ein anderer Zustand als "nicht
   * abgelehnt", und der wird hier auch anders benannt. Uebersetzt werden die
   * Codes bewusst NICHT: sie sind Googles Vokabular, und genau in dieser
   * Schreibweise stehen sie im Merchant Center, wo der Haendler sie repariert.
   */
  issues: readonly string[];
}

/**
 * Kennzeichnet ein Produkt, das Google beim juengsten taeglichen Abruf fuer
 * Shopping Ads abgelehnt hat (`products.shopping_ads_disapproved_at`,
 * Serving-Gate seit Migration `20260824090000`).
 *
 * WARUM DAS AUF DIESE SEITE GEHOERT. Ein abgelehntes Produkt wird nicht mehr
 * ausgeliefert, seine Nachher-Zahlen brechen also aus einem Grund ein, der mit
 * der Optimierung nichts zu tun hat. Bis hierher war das auf der Wirkungs-Seite
 * unsichtbar: die Zeile sah aus wie jede andere und zog Mittelwert, Median und
 * die klick-gewichtete Zahl nach unten, ohne dass irgendwo stand, warum. Auf
 * einem gemessenen Konto sind das 463 von 3.777 Produkten (12,3 %, Stand
 * 26.08.2026), auf einem anderen 106 von 72.015 (0,15 %) — die Groessenordnung
 * schwankt also um zwei Zehnerpotenzen und laesst sich nicht abschaetzen.
 *
 * BEWUSST NUR KENNZEICHNEN, NICHT FILTERN. Die abgelehnten Zeilen stillschweigend
 * aus der Tabelle zu nehmen wuerde die Kennzahlen der Seite aendern, ohne dass
 * es jemand merkt — und der Kunde verlöre genau die Liste, die er zum Reparieren
 * braucht.
 *
 * Farbe: `--warning-ink` auf `--warning-light`, in `src/lib/contrast.test.ts`
 * nachgerechnet. Kein Fehlerrot, denn das ist auf dieser Flaeche die Farbe des
 * negativen Vorzeichens (siehe `directionHonesty.sweep.test.tsx`), und die
 * Ablehnung ist keine Richtung, sondern ein Zustand. Das Dreieck traegt
 * `aria-hidden`, weil der Text daneben dieselbe Aussage macht: Farbe und Symbol
 * sind hier Beiwerk, nicht der Traeger der Information.
 */
export function DisapprovedBadge({ issues }: DisapprovedBadgeProps) {
  const { t } = useTranslation('performance');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid="disapproved-badge"
          className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-warning/30 bg-warning-light text-warning-ink"
        >
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          <span>{t('disapproved.badge')}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="space-y-1 text-xs">
          <div>{t('disapproved.tooltip')}</div>
          {issues.length > 0 ? (
            <div>
              {t('disapproved.issuesLabel')}: <span className="font-mono">{issues.join(', ')}</span>
            </div>
          ) : (
            <div>{t('disapproved.noIssues')}</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
